import React, { useState, useRef, useEffect } from 'react';
import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'zh', label: '中文', short: 'ZH' },
  { code: 'es', label: 'Espanol', short: 'ES' },
  { code: 'pt-BR', label: 'Portugues (BR)', short: 'PT' }
];

const LanguageSelector = ({ variant = 'full' }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const currentLang = i18n.language?.startsWith('pt') ? 'pt-BR' : (i18n.language?.startsWith('zh') ? 'zh' : (i18n.language?.startsWith('es') ? 'es' : 'en'));
  const currentLabel = LANGUAGES.find(l => l.code === currentLang)?.short || 'EN';

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const changeLang = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  // Compact nav dropdown version
  if (variant === 'nav') {
    return (
      <div className="relative" ref={ref} data-testid="language-nav-selector">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-[#FF6600] hover:bg-gray-800/30 transition-colors"
          data-testid="language-nav-toggle"
        >
          <Globe size={16} />
          <span>{currentLabel}</span>
        </button>
        {open && (
          <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 z-50 min-w-[150px]">
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => changeLang(lang.code)}
                data-testid={`lang-nav-${lang.code}`}
                className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                  currentLang === lang.code
                    ? 'bg-[#FF6600]/10 text-[#FF6600] font-semibold'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <span className="font-bold text-xs opacity-60 mr-2">{lang.short}</span>
                {lang.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Full settings version
  return (
    <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm" data-testid="language-settings">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
          <Globe size={20} className="text-blue-600" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900 dark:text-white">{t('settings.language')}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('settings.languageDesc')}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {LANGUAGES.map(lang => (
          <button
            key={lang.code}
            onClick={() => changeLang(lang.code)}
            data-testid={`lang-${lang.code}-btn`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              currentLang === lang.code
                ? 'bg-[#FF6600] text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <span className="font-bold text-xs opacity-70">{lang.short}</span>
            {lang.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default LanguageSelector;
